/*
 * File: BasicRichTextEditor.tsx
 * Author: Austin Robinson
 * Date Created: 1/28/2025
 * Copyright (c) DrivingDoc 2025
 */
import { ReactNode } from 'react';

import { AutoLinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { EditorThemeClasses, SerializedEditorState } from 'lexical';

import { MergeTagNode } from '../plugins';
import CoreEditor from './CoreEditor';

/**
 * Props for the BasicRichTextEditor component.
 *
 * @interface BasicRichTextEditorProps
 */
export interface BasicRichTextEditorProps {
  /**
   * The initial serialized state of the editor.
   *
   * @type {SerializedEditorState}
   */
  initialState?: SerializedEditorState;

  /**
   * Callback triggered when the editor state changes.
   * Receives the new value and whether the editor is dirty.
   *
   * @param {SerializedEditorState} value - The new editor state.
   * @param {boolean} isDirty - Whether the editor has unsaved changes.
   * @returns {void}
   */
  onChange?: (value: SerializedEditorState, isDirty: boolean) => void;

  /**
   * Required namespace for the Lexical editor.
   * Must be unique if multiple editors are on the same page.
   *
   * @type {string}
   */
  namespace: string;

  /**
   * Label for the editor. Can be a string (rendered as Typography) or a ReactNode.
   *
   * @type {string | ReactNode}
   */
  label?: string | ReactNode;

  /**
   * Placeholder text shown when the editor is empty.
   *
   * @type {string}
   */
  placeholder?: string;

  /**
   * Whether the editor is disabled.
   *
   * @type {boolean}
   */
  disabled?: boolean;

  /**
   * Whether the editor is in an error state.
   *
   * @type {boolean}
   */
  error?: boolean;

  /**
   * Helper text displayed below the editor.
   *
   * @type {ReactNode}
   */
  helperText?: ReactNode;

  /**
   * Whether the editor should take full width of its container.
   *
   * @type {boolean}
   */
  fullWidth?: boolean;

  /**
   * Minimum height of the editor in pixels.
   *
   * @type {number}
   */
  minHeight?: number;

  /**
   * Custom Lexical editor theme.
   *
   * @type {EditorThemeClasses}
   */
  theme?: EditorThemeClasses;

  /**
   * Merge tags available for insertion into the editor.
   *
   * @type {MergeTags}
   */
  mergeTags?: MergeTags;

  /**
   * Whether to show the save button in the toolbar.
   *
   * @type {boolean}
   */
  showSaveButton?: boolean;

  /**
   * Callback triggered when the save button is clicked.
   * Receives the current editor state.
   *
   * @param {SerializedEditorState | undefined} value - The current editor state.
   * @returns {void | Promise<void>}
   */
  onSave?: (value: SerializedEditorState | undefined) => void | Promise<void>;

  /**
   * Text for the save button.
   *
   * @type {string}
   */
  saveButtonText?: string;

  /**
   * Icon for the save button.
   *
   * @type {ReactNode}
   */
  saveButtonIcon?: ReactNode;

  /**
   * Whether the save operation is in progress.
   *
   * @type {boolean}
   */
  isSaving?: boolean;

  /**
   * Optional update metadata string to display below the editor.
   *
   * @type {string}
   */
  updateInfo?: string;

  /**
   * Whether the editor is loading.
   *
   * @type {boolean}
   */
  isLoading?: boolean;
}

/**
 * A reusable rich text editor component with a TextField-like API.
 * Provides internal isDirty tracking, optional save button, and validation display.
 *
 * @param {BasicRichTextEditorProps} props - Component props
 * @returns {JSX.Element} The BasicRichTextEditor component
 */
export function BasicRichTextEditor({
  initialState,
  onChange,
  namespace,
  label,
  placeholder = 'Enter text...',
  disabled = false,
  error = false,
  helperText,
  fullWidth = true,
  minHeight = 200,
  theme,
  mergeTags,
  showSaveButton = false,
  onSave,
  saveButtonText,
  saveButtonIcon,
  isSaving = false,
  updateInfo,
  isLoading = false,
}: BasicRichTextEditorProps) {

  const renderLabel = () => {
    if (!label) return null;
    return typeof label === 'string' ? (
      <Typography variant="subtitle2" fontWeight="bold" mb={1} color={error ? 'error' : 'text.primary'}>
        {label}
      </Typography>
    ) : (
      <Box mb={1}>{label}</Box>
    );
  };

  const initialConfig: InitialConfigType = {
    namespace: namespace,
    theme: theme,
    onError: console.error,
    nodes: [AutoLinkNode, HeadingNode, ListItemNode, ListNode, MergeTagNode, QuoteNode],
  };

  return (
    <Box
      sx={{
        // Control container width based on fullWidth prop
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {/* Optional label displayed above the editor */}
      {renderLabel()}

      <LexicalComposer initialConfig={initialConfig}>
        <CoreEditor
          initialState={initialState}
          placeholder={placeholder}
          error={error}
          disabled={disabled}
          minHeight={minHeight}
          isLoading={isLoading}
          mergeTags={mergeTags}
          showSaveButton={showSaveButton}
          saveButtonText={saveButtonText}
          saveButtonIcon={saveButtonIcon}
          isSaving={isSaving}
          onChange={onChange}
          onSave={onSave}
        />
      </LexicalComposer>

      {/* Helper text - displayed below editor, similar to TextField helperText */}
      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5, display: 'block' }}>
          {helperText}
        </Typography>
      )}

      {/* Update info - shows last updated timestamp and user (formatted externally) */}
      {updateInfo && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            mt: helperText ? 0.5 : 0.5,
            display: 'block',
            fontStyle: 'italic', // Italicized to distinguish from helper text
            opacity: 0.7, // Slightly faded to be less prominent
          }}
        >
          {updateInfo}
        </Typography>
      )}
    </Box>
  );
}

export default BasicRichTextEditor;
